import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import DashboardLayout from "@/components/DashboardLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import ConfirmDialog from "@/components/ConfirmDialog";
import { Checkbox } from "@/components/ui/checkbox";
import { Switch } from "@/components/ui/switch";
import { UserPlus, Trash2, ShieldCheck, Mail, Lock } from "lucide-react";
import { ALL_PERMS, PERM_LABELS, effOf } from "@/lib/perms";

export default function Team() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const feat = effOf(user, null);
  const maxAssistants = user?.is_super_admin ? 9999 : (feat.max_assistants || 0);
  const isFull = user?.is_super_admin || maxAssistants > 0;
  const [members, setMembers] = useState([]);
  const [raffles, setRaffles] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ email: "", name: "", permissions: [], assigned_raffles: null });
  const [saving, setSaving] = useState(false);
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [delMember, setDelMember] = useState(null);
  const [delLoading, setDelLoading] = useState(false);

  const load = async () => {
    try { const r = await api.get("/team"); setMembers(r.data); } catch (e) { console.error("No se pudo cargar el equipo:", e); }
    try { const r = await api.get("/raffles"); setRaffles(r.data); } catch (e) { console.error("No se pudieron cargar los eventos:", e); }
  };
  useEffect(() => { load(); }, []);

  const openNew = () => { setEditing(null); setAcceptedTerms(false); setForm({ email: "", name: "", permissions: ["verify_payments"], assigned_raffles: null }); setOpen(true); };  const openEdit = (m) => { setEditing(m); setForm({ email: m.email, name: m.name || "", permissions: m.permissions || [], assigned_raffles: m.assigned_raffles ?? null }); setOpen(true); };

  const toggleCrm = async (m, enabled) => {
    setMembers((prev) => prev.map((x) => x.user_id === m.user_id ? { ...x, crm_access: enabled } : x));
    try {
      await api.patch(`/admin/assistants/${m.user_id}/crm-access`, { enabled });
      toast.success(enabled ? `${m.name || "Asistente"} ya tiene acceso al CRM` : `Acceso al CRM revocado`);
    } catch (e) {
      setMembers((prev) => prev.map((x) => x.user_id === m.user_id ? { ...x, crm_access: !enabled } : x));
      toast.error(e?.response?.data?.detail || "No se pudo actualizar el acceso");
    }
  };

  const togglePerm = (p) => setForm((f) => ({ ...f, permissions: f.permissions.includes(p) ? f.permissions.filter((x) => x !== p) : [...f.permissions, p] }));
  const setLimitEvents = (limit) => setForm((f) => ({ ...f, assigned_raffles: limit ? (Array.isArray(f.assigned_raffles) ? f.assigned_raffles : []) : null }));
  const toggleRaffle = (id) => setForm((f) => {
    const cur = Array.isArray(f.assigned_raffles) ? f.assigned_raffles : [];
    return { ...f, assigned_raffles: cur.includes(id) ? cur.filter((x) => x !== id) : [...cur, id] };
  });

  const save = async () => {
    setSaving(true);
    try {
      if (editing) {
        await api.put(`/team/${editing.user_id}`, { name: form.name, permissions: form.permissions, assigned_raffles: form.assigned_raffles });
        toast.success("Asistente actualizado");
      } else {
        if (!form.email) { toast.error("Ingresa el correo"); setSaving(false); return; }
        await api.post("/team", form);
        toast.success("Asistente agregado. Podrá ingresar con su cuenta de Google.");
      }
      setOpen(false); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Error al guardar"); }
    finally { setSaving(false); }
  };

  const remove = async () => {
    if (!delMember) return;
    setDelLoading(true);
    try { await api.delete(`/team/${delMember.user_id}`); toast.success("Asistente eliminado"); setDelMember(null); load(); }
    catch (e) { toast.error("Error"); }
    finally { setDelLoading(false); }
  };

  return (
    <DashboardLayout title="Equipo">
      <div className="flex items-center justify-between mb-5 gap-3 flex-wrap">
        <p className="text-sm text-neutral-500 max-w-lg">Agrega asistentes con permisos limitados para ayudarte a verificar pagos y controlar tus sorteos. Ingresan con su propia cuenta de Google y solo ven tus rifas.</p>
        {isFull ? (
          <Button data-testid="add-member-btn" onClick={openNew} className="bg-[#00AEEF] hover:bg-[#0092CC] rounded-xl"><UserPlus size={16} className="mr-1" /> Agregar asistente</Button>
        ) : (
          <Button data-testid="add-member-locked-btn" variant="outline" onClick={() => { toast.info("Disponible con Acceso Completo. Solicítalo desde tu panel."); navigate("/dashboard"); }} className="rounded-xl gap-2 text-neutral-500 border-dashed"><Lock size={15} /> Agregar asistente</Button>
        )}
      </div>

      {members.length === 0 ? (
        <div className="bg-white rounded-2xl border border-dashed border-neutral-300 p-12 text-center">
          <div className="w-14 h-14 rounded-2xl bg-[#E6F7FE] text-[#00AEEF] flex items-center justify-center mx-auto mb-4"><UserPlus size={26} /></div>
          <h3 className="font-display font-bold text-lg">Sin asistentes aún</h3>
          <p className="text-neutral-500 mt-1">Agrega personas de confianza con permisos específicos.</p>
        </div>
      ) : (
        <div className="grid md:grid-cols-2 gap-4">
          {members.map((m) => (
            <div key={m.user_id} data-testid={`member-${m.user_id}`} className="bg-white rounded-2xl border border-neutral-200 p-5">
              <div className="flex items-start justify-between">
                <div className="min-w-0">
                  <p className="font-semibold truncate">{m.name}</p>
                  <p className="text-sm text-neutral-500 flex items-center gap-1"><Mail size={13} /> {m.email}</p>
                </div>
                <span className="text-xs font-semibold px-2 py-1 rounded-full bg-[#E6F7FE] text-[#0092CC]">Asistente</span>
              </div>
              <div className="flex flex-wrap gap-1.5 mt-3">
                {(m.permissions || []).length === 0 ? <span className="text-xs text-neutral-400">Sin permisos (solo lectura)</span>
                  : m.permissions.map((p) => <span key={p} className="text-xs px-2 py-1 rounded-lg bg-neutral-100 text-neutral-600">{PERM_LABELS[p]}</span>)}
              </div>
              <p className="text-xs text-neutral-500 mt-2" data-testid={`member-events-${m.user_id}`}>
                {Array.isArray(m.assigned_raffles)
                  ? `Acceso a ${m.assigned_raffles.length} evento(s) específico(s)`
                  : "Acceso a todos tus eventos"}
              </p>
              {user?.is_super_admin && (
                <div className="mt-3 flex items-center justify-between gap-2 rounded-xl bg-neutral-50 border border-neutral-200 px-3 py-2">
                  <div className="min-w-0">
                    <p className="text-xs font-semibold">Acceso completo al CRM</p>
                    <p className="text-[11px] text-neutral-500">Clientes y envío de campañas de correo</p>
                  </div>
                  <Switch data-testid={`crm-access-${m.user_id}`} checked={!!m.crm_access}
                    onCheckedChange={(v) => toggleCrm(m, !!v)} />
                </div>
              )}
              <div className="flex gap-2 mt-4 pt-4 border-t border-neutral-100">
                <Button variant="outline" size="sm" data-testid={`edit-member-${m.user_id}`} onClick={() => openEdit(m)} className="rounded-lg flex-1">Editar permisos</Button>
                <Button variant="ghost" size="sm" data-testid={`remove-member-${m.user_id}`} onClick={() => setDelMember(m)} className="rounded-lg text-red-500"><Trash2 size={16} /></Button>
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>{editing ? "Editar asistente" : "Agregar asistente"}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            {!editing && (
              <div>
                <Label>Correo (cuenta de Google)</Label>
                <Input data-testid="member-email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="asistente@gmail.com" className="mt-1 rounded-lg" />
                <p className="text-xs text-neutral-400 mt-1" data-testid="member-email-hint">El Asistente debe iniciar sesión con una cuenta de Google. Asegúrate de ingresar su correo de Gmail.</p>
              </div>
            )}
            <div>
              <Label>Nombre</Label>
              <Input data-testid="member-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Nombre del asistente" className="mt-1 rounded-lg" />
            </div>
            <div>
              <Label className="mb-2 block">Permisos</Label>
              <div className="space-y-2">
                {ALL_PERMS.map((p) => (
                  <label key={p} className="flex items-center gap-3 rounded-lg border border-neutral-200 p-3 cursor-pointer hover:bg-neutral-50">
                    <Checkbox data-testid={`perm-${p}`} checked={form.permissions.includes(p)} onCheckedChange={() => togglePerm(p)} />
                    <span className="text-sm">{PERM_LABELS[p]}</span>
                  </label>
                ))}
              </div>
            </div>
            <div>
              <Label className="mb-2 block">Acceso a eventos</Label>
              <label className="flex items-center gap-3 rounded-lg border border-neutral-200 p-3 cursor-pointer hover:bg-neutral-50">
                <Checkbox data-testid="limit-events-toggle" checked={Array.isArray(form.assigned_raffles)} onCheckedChange={(v) => setLimitEvents(!!v)} />
                <span className="text-sm">Limitar a eventos específicos <span className="text-neutral-400">(si no, verá todos tus eventos)</span></span>
              </label>
              {Array.isArray(form.assigned_raffles) && (
                <div className="mt-2 space-y-1.5 max-h-48 overflow-auto rounded-lg border border-neutral-200 p-2" data-testid="assigned-raffles-list">
                  {raffles.length === 0 ? (
                    <p className="text-xs text-neutral-400 p-2">Aún no tienes eventos para asignar.</p>
                  ) : raffles.map((r) => (
                    <label key={r.id} className="flex items-center gap-3 rounded-md p-2 cursor-pointer hover:bg-neutral-50">
                      <Checkbox data-testid={`assign-raffle-${r.id}`} checked={(form.assigned_raffles || []).includes(r.id)} onCheckedChange={() => toggleRaffle(r.id)} />
                      <span className="text-sm truncate">{r.title}</span>
                    </label>
                  ))}
                </div>
              )}
            </div>
          </div>
          {!editing && (
            <label className="flex items-start gap-2.5 mt-4 cursor-pointer">
              <Checkbox data-testid="member-accept-terms" checked={acceptedTerms} onCheckedChange={(v) => setAcceptedTerms(!!v)} className="mt-0.5" />
              <span className="text-xs text-neutral-600 leading-relaxed">He leído y acepto los <a href="/terminos" target="_blank" rel="noreferrer" className="font-semibold text-[#0092CC] underline">Términos y Condiciones</a></span>
            </label>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} className="rounded-xl">Cancelar</Button>
            <Button data-testid="save-member-btn" onClick={save} disabled={saving || (!editing && !acceptedTerms)} className="bg-[#00AEEF] hover:bg-[#0092CC] rounded-xl"><ShieldCheck size={16} className="mr-1" />{saving ? "Guardando..." : "Guardar"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ConfirmDialog
        open={!!delMember}
        onOpenChange={(v) => !v && setDelMember(null)}
        title="¿Desea realmente realizar esta acción?"
        description={delMember ? `Se eliminará al asistente ${delMember.name || delMember.email} y perderá el acceso a tu cuenta.` : ""}
        confirmText="Sí, eliminar asistente"
        onConfirm={remove}
        loading={delLoading}
      />
    </DashboardLayout>
  );
}
